import { Stack } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { TBaseIcon } from '@/shared/assets/icons/base-icon/base-icon.types';
import { NotificationIcon } from '@/shared/assets/icons/notification-icon';
import { OpenIcon } from '@/shared/assets/icons/open-icon';
import { ReadNotificationIcon } from '@/shared/assets/icons/read-notification-icon';
import { UnreadNotificationIcon } from '@/shared/assets/icons/unread-notification-icon';
import { IconBgButton } from '@/shared/components/ui/button/icon-bg-button/icon-bg-button';
import { Chip } from '@/shared/components/ui/chip/chip';
import { ResourceIcon } from '@/shared/components/ui/resource-icon/resource-icon';
import { ResourceTypeEnum } from '@/shared/components/ui/resource-icon/resource-icon.types';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { useReadPipelineQuery } from '@/shared/redux/api/jobs/jobs-api';
import { useUpdateNotificationReadStatusMutation } from '@/shared/redux/api/notifications/notifications-api';
import { isPermissionDeniedError } from '@/shared/redux/api/utils/error-handlers';
import { TJobStatus } from '@/shared/types/job-status.types';
import { NotificationFragment } from '@/shared/types/types.generated';
import { getProtectedResourceLabel } from '@/shared/utils/protected-resource-label';

export const useNotification = (notification?: NotificationFragment) => {
  const { consoleConfiguration } = notification || {};
  const { entityType, readStatus, consoleEndpoint } =
    consoleConfiguration || {};

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getStatusColor, getStatusLabel } = useJobStatus();
  const [updateReadStatus, { isLoading: isUpdating }] =
    useUpdateNotificationReadStatusMutation();
  const {
    data: pipelineData,
    error: pipelineError,
    isLoading: pipelineLoading,
    isFetching: pipelineFetching,
  } = useReadPipelineQuery(
    { pipelineConfigurationId: consoleConfiguration?.entityId || '' },
    {
      skip:
        !consoleConfiguration?.entityId &&
        entityType !== ResourceTypeEnum.Pipeline,
    },
  );

  const renderIcon = (
    notification?: NotificationFragment,
    iconProps?: TBaseIcon,
  ) => {
    const entityType = notification?.consoleConfiguration?.entityType;
    if (!entityType) {
      return <NotificationIcon variant="primary" size="1.25rem" />;
    }

    return (
      <ResourceIcon
        resourceType={entityType as ResourceTypeEnum}
        iconProps={{ variant: 'primary', size: '1.25rem', ...iconProps }}
      />
    );
  };

  const getEntityName = () => {
    if (
      entityType === ResourceTypeEnum.Pipeline &&
      pipelineData?.readPipelineConfiguration?.name
    ) {
      return `${pipelineData?.readPipelineConfiguration?.name}: `;
    }
    if (
      entityType === ResourceTypeEnum.Pipeline &&
      !pipelineLoading &&
      !pipelineFetching &&
      isPermissionDeniedError(pipelineError)
    ) {
      return `${getProtectedResourceLabel(t, 'pipeline')}: `;
    }
    return undefined;
  };

  const renderMessage = (
    notification?: NotificationFragment,
    entityName: string = '',
  ) => {
    const { entityType, action, fallbackMessage, status } =
      notification?.consoleConfiguration || {};

    if (!entityType || !action) {
      return fallbackMessage ?? t('notifications:new_notification_arrived');
    }

    return (
      <Trans
        i18nKey={`notifications:actions.${entityType?.toLowerCase()}.${action}`}
        values={{ entityName }}
        components={{
          status: (
            <Chip
              status={getStatusColor(status as TJobStatus)}
              label={getStatusLabel(status as TJobStatus)}
              withDot
            />
          ),
        }}
      />
    );
  };

  const renderNavigateToButton = () => {
    if (!consoleEndpoint) {
      return null;
    }

    const to = new URL(consoleEndpoint).pathname;
    return (
      <IconBgButton
        size="extraSmall"
        variant="secondary"
        onClick={() => navigate(to)}
        tooltip={t('notifications:view_details')}
      >
        <OpenIcon />
      </IconBgButton>
    );
  };

  const handleReadUnreadToggle = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    if (!notification || isUpdating) {
      return;
    }

    try {
      await updateReadStatus({
        notificationId: notification.notificationId,
        readStatus: !readStatus,
      }).unwrap();
    } catch {
      console.error('Failed to update notification-row read status');
    }
  };

  const renderReadUnreadToggleButton = () => {
    const tooltip = readStatus
      ? t('notifications:mark_as_unread')
      : t('notifications:mark_as_read');

    return (
      <IconBgButton
        size="extraSmall"
        variant="secondary"
        tooltip={tooltip}
        onClick={handleReadUnreadToggle}
        loading={isUpdating}
      >
        {readStatus ? <UnreadNotificationIcon /> : <ReadNotificationIcon />}
      </IconBgButton>
    );
  };

  const renderActions = () => {
    return (
      <Stack direction="row" alignItems="center" gap="0.75rem">
        {renderNavigateToButton()}
        {renderReadUnreadToggleButton()}
      </Stack>
    );
  };

  return {
    icon: renderIcon(notification),
    message: renderMessage(notification, getEntityName()),
    read: readStatus,
    actions: renderActions(),
    renderMessage,
    renderIcon,
  };
};
